import { useState } from 'react';
import { appRoutesPaths } from '../../../settings/appConfig';
import useMainInfoForm from './useMainInfoForm';
import { useNavigate } from 'react-router';
import useModal from '../../UI/useModal';
import useValidation from '../../validation/useValidation';
import useUI from '../../context/useUI';
import usePostCustodian from '../usePostCustodian';
import useCheckDuplicatePhonesAndEmails from '../useCheckDuplicatePhonesAndEmails';
import Dropdown from '../../../Models/Locations/dropdown';
import {
  isObjectEmpty,
  validateAndClearSingleItemArray,
} from '../../../utils/validationHelpers';
import { handleDynamicValidation } from '../../../utils/entitiesValidationHelper';
import { copyAddressFields } from '../../../utils/formUtils';

export default function useAddCustodianWizard({
  states,
  custodianTypes,
  phoneTypes,
  portalTypes,
  sourceTypes,
  recordTypes,
  supportedOrderTypes,
  departmentFileTypes,
  followUpMethods,
}: {
  states: any[];
  custodianTypes: any[];
  phoneTypes: any[];
  portalTypes: any[];
  sourceTypes: any[];
  recordTypes: any[];
  supportedOrderTypes: any[];
  departmentFileTypes: any[];
  followUpMethods: Dropdown[];
}) {
  const { save } = usePostCustodian();
  const { hasDuplicatePhoneOrEmailError } = useCheckDuplicatePhonesAndEmails();

  const { mainInfoForm } = useMainInfoForm({
    custodianTypes: custodianTypes,
    phoneTypes: phoneTypes,
    recordTypes: recordTypes,
    portalTypes: portalTypes,
    sourceTypes: sourceTypes,
    states: states,
  });

  const [saving, setSaving] = useState<boolean>(false);

  const isValid = async () => {
    mainInfoForm.submitForm();
    let mainFormErrors = await mainInfoForm.validateForm(mainInfoForm.values);
    return (
      Object.keys(mainFormErrors).length === 0 &&
      !hasDuplicatePhoneOrEmailError(mainInfoForm)
    );
  };

  const { isPhoneValid, isPortalValid, isContactValid, isEmailValid } =
    useValidation();
  const { showSnackbar } = useUI();

  // Helper functions to reduce complexity with types
  const filterAndMap = <T, U>(
    arr: T[],
    validateFn: (item: T) => boolean,
    mapFn: (item: T) => U,
  ): U[] | undefined => {
    return arr?.filter(validateFn)?.map(mapFn);
  };

  const handleCustodianBilling = (custodianDetails: any) => {
    if (custodianDetails) {
      const billing = custodianDetails.billingInformation;
      const isBillingEmpty =
        billing &&
        Object.values(billing).every((v) => {
          return (
            v === '' ||
            v === null ||
            v === false ||
            (Array.isArray(v) && v.length === 0)
          );
        });

      if (isBillingEmpty) {
        custodianDetails.billingInformation = null;
      }
    }
  };

  const handleDepartmentPhysicians = (departmentAndPhysicians: any[]) => {
    if (
      Array.isArray(departmentAndPhysicians) &&
      departmentAndPhysicians.length === 1
    ) {
      const onlyItem = departmentAndPhysicians[0];
      const isItemEmpty = Object.values(onlyItem).every(
        (v) => v === null || v === '',
      );

      if (isItemEmpty) {
        return null;
      }
    }
    return departmentAndPhysicians;
  };

  const handleMailingAddress = (mailingAddress: any) => {
    return isObjectEmpty(mailingAddress) ? null : mailingAddress;
  };

  const handleExternalSourceMappings = (externalSourceMappings: any[]) => {
    const mappings = validateAndClearSingleItemArray(externalSourceMappings);
    return mappings
      ? mappings.map((m) => ({
          mappedId: m.mappedId,
          sourceTypeId: m.sourceTypeId,
          notes: m.notes,
        }))
      : null;
  };

  // Main saveClickHandler refactor
  const saveClickHandler = async (): Promise<boolean> => {
    const isValidForm = await isValid();
    const isDynamicFormValid = handleDynamicValidation(
      mainInfoForm.values,
      mainInfoForm,
    );

    if (isValidForm === true && isDynamicFormValid === true) {
      const phones = filterAndMap(
        mainInfoForm.values.phones,
        isPhoneValid,
        (p) => ({
          isPrimary: p.isPrimary,
          phoneNumber: p.phoneNumber,
          phoneTypeId: p.phoneTypeId,
        }),
      );

      const emails = filterAndMap(
        mainInfoForm.values.emails,
        isEmailValid,
        (e : any) => ({
          email: e.email,
        }),
      );

      const portalUrls = filterAndMap(
        mainInfoForm.values.portalUrls,
        isPortalValid,
        (p) =>
          mainInfoForm.values.isCustodian
            ? {
                portalUrl: p.portalUrl,
                portalTypeId: p.portalTypeId,
                portalNotes: p.portalNotes,
              }
            : { portalUrl: p.portalUrl },
      );

      const externalSourceMappings = handleExternalSourceMappings(
        mainInfoForm.values.externalSourceMappings,
      );
      const contacts = mainInfoForm.values.contacts?.filter((e : any) =>
        isContactValid(e),
      );

      const custodianDetails = mainInfoForm?.values?.isCustodian
        ? { ...mainInfoForm?.values?.custodianDetails }
        : null;

      handleCustodianBilling(custodianDetails);

      let departmentAndPhysicians = handleDepartmentPhysicians(
        mainInfoForm.values.departmentAndPhysicians,
      );
      const mailingAddress = handleMailingAddress(
        mainInfoForm.values?.mailingAddress,
      );

      const model = {
        ...mainInfoForm.values,
        mailingAddress,
        custodianDetails,
        portalUrls,
        externalSourceMappings,
        phones,
        emails,
        contacts,
        departmentAndPhysicians,
      };

      try {
        setSaving(true);
        await save(model);
        mainInfoForm.resetForm();
      } catch {
        showSnackbar(['Saving failed, Please try again'], 'error');
      } finally {
        setSaving(false);
      }
      return true;
    } else {
      showSnackbar(['Please fix the validation errors first'], 'error');
    }
    return false;
  };

  const nav = useNavigate();
  const { handleCloseModal, handleOpenModal, isModalOpened } = useModal();
  const discard = () => {
    nav(appRoutesPaths.entities);
    handleCloseModal();
  };
  const cancelClickHandler = () => {
    //show discard confirmation
    handleOpenModal();
  };

  const copyCustodianAddress = (formKeyName: string) => {
    copyAddressFields(
      mainInfoForm.setFieldValue,
      formKeyName,
      mainInfoForm.values.addressInfo,
    );
  };

  return {
    cancelClickHandler,
    saveClickHandler,
    mainInfoForm,
    isDiscardModalOpened: isModalOpened,
    discard,
    cancelDiscard: handleCloseModal,
    isSaving: saving,
    copyCustodianAddress,
    followUpMethods,
  };
}
